import useAccountManagementStore from "../stores/accountManagementStore";
import useDataStore, { DataStoreType } from "../stores/dataStore";
import useMainStore, { MainStoreType } from "../stores/mainStore";
import useSettingsStore, { SettingsStore } from "../stores/settingsStore";
import useTextFilesStore from "../stores/textFilesStore";
import { ClearSoftwareData } from "./cache/clearAllCache";
import { saveDataStore } from "./cache/manageDataStore";
import { saveMainStore } from "./cache/manageMainStore";
import { loadTextFile, saveTextFile } from "./cache/manageTextFile";
import { htmlToPlainText } from "./htmlToPlainText";
import { Editor } from "@tiptap/react";

type ExportData = {
    mainStore?: MainStoreType["data"];
    dataStore?: DataStoreType["data"];
    settingsStore?: SettingsStore["data"];
    textFiles?: {
        [key: string]: string;
    };
};

export async function ExportAllData() {
    let Data: ExportData = {};

    const mainStore = useMainStore.getState().data;
    const dataStore = useDataStore.getState().data;
    const settingsStore = useSettingsStore.getState().data;

    // Get text files
    const textFileIDs = Object.keys(useDataStore.getState().data.notes);
    const textFiles: { [key: string]: string } = {};

    // const userID = useAccountManagementStore.getState().userData?.id;

    await Promise.all(
        textFileIDs.map(async (id) => {
            const idName = id.split("/").pop()?.trim() || "";
            textFiles[idName] = await loadTextFile(id);
        })
    );

    Data["mainStore"] = mainStore;
    Data["dataStore"] = dataStore;
    Data["settingsStore"] = settingsStore;
    Data["textFiles"] = textFiles;

    // For some reason I need to timeout for it to export correctly
    const dataStr = JSON.stringify(Data);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;

    const fileName = `CloudNote_Backup_${getFormattedDate()}.json`;

    link.setAttribute("download", fileName);
    link.click();
    window.URL.revokeObjectURL(url);
}

export async function ImportAllData() {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".json";
    fileInput.addEventListener("change", async (e) => {
        const file = (e.target as HTMLInputElement)?.files?.[0];
        if (!file) return;
        const fileReader = new FileReader();
        fileReader.onload = async () => {
            const data: ExportData = JSON.parse(fileReader.result as string);
            const userID = useAccountManagementStore.getState().userData?.id;

            // Format data
            data.mainStore.userID = userID;

            // Clear cache before loading project
            await ClearSoftwareData(true);

            if (data.mainStore)
                useMainStore
                    .getState()
                    .set.data(() => data.mainStore as MainStoreType["data"]);
            if (data.dataStore)
                useDataStore
                    .getState()
                    .set.data(() => data.dataStore as DataStoreType["data"]);
            if (data.settingsStore)
                useSettingsStore
                    .getState()
                    .set.data(
                        () => data.settingsStore as SettingsStore["data"]
                    );
            if (data.textFiles) {
                const fileIDs = Object.keys(data.textFiles);
                if (fileIDs.length === 0) return;

                await Promise.all(
                    fileIDs.map(async (id) => {
                        if (!id || !data.textFiles || !data.textFiles[id])
                            return;
                        await saveTextFile(id, data.textFiles[id] as string);
                    })
                );
            }

            saveMainStore(userID);
            saveDataStore(userID, true);
        };
        fileReader.readAsText(file);
    });
    fileInput.click();
}

function getFormattedDate() {
    const date = new Date();
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hour = date.getHours().toString().padStart(2, "0");
    const minute = date.getMinutes().toString().padStart(2, "0");
    const second = date.getSeconds().toString().padStart(2, "0");

    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
}

export async function exportNoteData(noteID: string, isPlainText: boolean) {
    const fileName = useDataStore.getState().data.notes[noteID]?.title || "";

    let textContent = await useTextFilesStore
        .getState()
        .getTextFile(noteID)
        .then((textFile) => textFile?.content);
    if (!textContent) return;

    if (isPlainText) {
        textContent = htmlToPlainText(textContent || "");
    }

    // Save file to desktop

    const blob = new Blob([textContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.txt`;
    link.click();
    URL.revokeObjectURL(url);
}

export async function importNoteFile(
    noteID: string | undefined,
    editor: Editor | null
) {
    const selectedNoteID = useMainStore.getState().data.selectedNoteID;
    const selectedFolderID = useMainStore.getState().data.selectedFolderID;
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".txt";
    fileInput.style.display = "none";
    document.body.appendChild(fileInput);

    fileInput.addEventListener("change", async (e: any) => {
        const file = e.target.files[0];
        if (!file) return;
        const fileName = file.name.split(".")[0];

        const fileReader = new FileReader();
        fileReader.onload = async (e) => {
            const textContent = e.target?.result as string;
            if (!textContent) return;

            if (editor && selectedNoteID === noteID) {
                editor.commands.setContent(textContent, false);
                useTextFilesStore.getState().saveContent(noteID, textContent);
            } else {
                if (noteID) {
                    useTextFilesStore
                        .getState()
                        .saveContent(noteID, textContent);
                }
                // If no noteID passed (when importing on left sidebar)
                else {
                    // add note to dataStore
                    const noteID = useDataStore.getState().add.note(
                        {
                            title: fileName,
                            folderID: selectedFolderID,
                        },
                        true
                    );

                    // add content to cache
                    useTextFilesStore
                        .getState()
                        .addTextFile(noteID, textContent);
                }
            }
        };
        fileReader.readAsText(file);
    });
    fileInput.click();
}